// src/components/analytics/PredictorFactory.js

import { LinearRegressionPredictor } from './predictors/LinearRegressionPredictor';
import { MovingAveragePredictor } from './predictors/MovingAveragePredictor';
import { ARIMAPredictor } from './predictors/ARIMAPredictor';
import { ExponentialSmoothingPredictor } from './predictors/ExponentialSmoothingPredictor';


/**
 * Factory class for creating predictor instances based on method type
 */
export class PredictorFactory {
  /**
   * Map of available predictor types to their classes
   */
  static predictorMap = {
    'linear_regression': LinearRegressionPredictor,
    'linearRegression': LinearRegressionPredictor, // Alternative naming
    'moving_average': MovingAveragePredictor,
    'movingAverage': MovingAveragePredictor, // Alternative naming
    'arima': ARIMAPredictor,
    'ARIMA': ARIMAPredictor, // Alternative naming
    'exponential_smoothing': ExponentialSmoothingPredictor,
    'exponentialSmoothing': ExponentialSmoothingPredictor, // Alternative naming
    // Future predictors can be added here:
    // 'polynomial': PolynomialPredictor,
    // 'seasonal_decomposition': SeasonalDecompositionPredictor
  };

  /*───────────────────────────────────────────────────────────────
  CREATE OR – if config.autoPick === true – EVALUATE & PICK
  ───────────────────────────────────────────────────────────────*/
  static createPredictor(method, config = {}) {
    // ── simple one-off mode ─────────────────────────────────────
    if (!config.autoPick) {
      const PredictorClass = this.getPredictorClass(method);
      return new PredictorClass(config);
    }

    // ── auto-select mode: race ARIMA vs Exponential Smoothing ───
    const candidates = ['arima', 'exponential_smoothing'];
    let best = { smape: Infinity, predictor: null };

    for (const cand of candidates) {
      const PredictorClass = this.getPredictorClass(cand);
      const predictor = new PredictorClass({ ...config, autoPick: false }); // avoid recursion
      const metrics = predictor.backtest(config.values);
      console.log(`${cand} → sMAPE=${metrics.smape.toFixed(2)}%`);
      if (metrics.smape < best.smape) best = { smape: metrics.smape, predictor };
    }

    console.log(`Auto-picked ${best.predictor.constructor.name} (sMAPE ${best.smape.toFixed(2)}%)`);
    return best.predictor;
  }

  // helper pulled out for clarity – unchanged behaviour
  static getPredictorClass(name) {
    if (!name) throw new Error('Prediction method must be specified');
    const key = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const PredictorClass = this.predictorMap[key] || this.predictorMap[name];
    if (!PredictorClass) {
      const list = Object.keys(this.predictorMap).join(', ');
      throw new Error(`Unsupported prediction method: "${name}". Available: ${list}`);
    }
    return PredictorClass;
  }

  /**
   * Checks if a prediction method is supported
   * @param {string} method - The method to check
   * @returns {boolean} - True if method is supported
   */
  static isMethodSupported(method) {
    if (!method) return false;
    
    const normalizedMethod = method.toLowerCase().replace(/[^a-z0-9]/g, '_');
    return !!(this.predictorMap[normalizedMethod] || this.predictorMap[method]);
  }

  /**
   * Gets list of available prediction methods
   * @returns {Array<string>} - Array of supported method names
   */
  static getAvailableMethods() {
    // Return only the canonical method names (snake_case versions)
    const uniqueMethods = new Set();
    
    // Define the canonical names for each predictor
    const canonicalNames = {
      'linear_regression': true,
      'moving_average': true,
      'arima': true,
      'exponential_smoothing': true
    };
    
    // Only return the canonical method names
    return Object.keys(this.predictorMap).filter(method => canonicalNames[method]);
  }

  /**
   * Gets metadata about a specific prediction method
   * @param {string} method - The method to get info for
   * @returns {Object|null} - Method metadata or null if not found
   */
  static getMethodInfo(method) {
    const methodInfoMap = {
      'linear_regression': {
        name: 'Linear Regression',
        description: 'Fits a straight line through data points to predict future values',
        suitable_for: ['Trending data', 'Time series with linear growth/decline'],
        parameters: {
          numPredictions: 'Number of future points to predict',
          predictUntil: 'Date to predict until (for time series)',
          includeConfidenceInterval: 'Whether to include confidence bands',
          confidenceLevel: 'Confidence level (e.g., 0.95 for 95%)'
        }
      },
      'moving_average': {
        name: 'Moving Average',
        description: 'Calculates average over a sliding window to smooth data and predict trends',
        suitable_for: ['Noisy data', 'Short-term forecasting', 'Trend smoothing'],
        parameters: {
          windowSize: 'Number of periods in the moving window (default: 3)',
          numPredictions: 'Number of future points to predict',
          predictUntil: 'Date to predict until (for time series)',
          predictionMethod: 'Method for prediction: simple, weighted, or trend',
          includeHistoricalMA: 'Include historical moving averages in output',
          includeConfidenceInterval: 'Whether to include confidence bands',
          confidenceLevel: 'Confidence level (e.g., 0.95 for 95%)'
        }
      },
      'arima': {
        name: 'ARIMA (AutoRegressive Integrated Moving Average)',
        description: 'Advanced time series forecasting that combines autoregression, differencing, and moving averages',
        suitable_for: ['Complex time series', 'Data with trends', 'Seasonal patterns', 'Financial forecasting'],
        parameters: {
          p: 'Order of autoregression (default: 1)',
          d: 'Degree of differencing (default: 1)', 
          q: 'Order of moving average (default: 1)',
          numPredictions: 'Number of future points to predict',
          predictUntil: 'Date to predict until (for time series)',
          resolution: 'Aggregation resolution: daily, weekly, monthly, yearly',
          aggregationMethod: 'How to aggregate: sum, average, max, min (default: sum)',
          includeConfidenceInterval: 'Whether to include prediction intervals',
          confidenceLevel: 'Confidence level (e.g., 0.95 for 95%)'
        }
      },
      'exponential_smoothing': {
        name: 'Exponential Smoothing (Holt-Winters)',
        description: 'Time series forecasting that gives more weight to recent observations, with options for trend and seasonality',
        suitable_for: ['Business forecasting', 'Sales data', 'Inventory planning', 'Short to medium-term predictions'],
        parameters: {
          smoothingMethod: 'Method: simple, double, triple, or auto (default: auto)',
          alpha: 'Level smoothing parameter (0-1, default: 0.3)',
          beta: 'Trend smoothing parameter (0-1, default: 0.1)',
          gamma: 'Seasonal smoothing parameter (0-1, default: 0.1)',
          seasonalPeriod: 'Seasonal period if known (e.g., 12 for monthly data with yearly seasonality)',
          seasonalType: 'Type of seasonality: additive or multiplicative (default: additive)',
          numPredictions: 'Number of future points to predict',
          predictUntil: 'Date to predict until (for time series)',
          includeConfidenceInterval: 'Whether to include confidence bands',
          confidenceLevel: 'Confidence level (e.g., 0.95 for 95%)'
        }
      }
      // Future methods info can be added here
    };

    const normalizedMethod = method.toLowerCase().replace(/[^a-z0-9]/g, '_');
    return methodInfoMap[normalizedMethod] || methodInfoMap[method] || null;
  }

  /**
   * Gets human-readable display names for predictors
   * @returns {Object} - Map of method keys to display names
   */
  static getMethodDisplayNames() {
    return {
      'linear_regression': 'Linear Regression',
      'linearRegression': 'Linear Regression',
      'moving_average': 'Moving Average',
      'movingAverage': 'Moving Average',
      'arima': 'ARIMA',
      'ARIMA': 'ARIMA',
      'exponential_smoothing': 'Exponential Smoothing',
      'exponentialSmoothing': 'Exponential Smoothing'
    };
  }

  /**
   * Validates predictor configuration
   * @param {string} method - The prediction method
   * @param {Object} config - Configuration to validate
   * @returns {Object} - { valid: boolean, errors: Array<string> }
   */
  static validateConfig(method, config) {
    const errors = [];
    
    // Common validations
    if (config.numPredictions && (typeof config.numPredictions !== 'number' || config.numPredictions < 1)) {
      errors.push('numPredictions must be a positive number');
    }

    if (config.confidenceLevel && (config.confidenceLevel <= 0 || config.confidenceLevel >= 1)) {
      errors.push('confidenceLevel must be between 0 and 1');
    }

    // Method-specific validations
    if (method === 'linear_regression' || method === 'linearRegression') {
      // Linear regression specific validations
      if (config.predictUntil && typeof config.predictUntil !== 'string') {
        errors.push('predictUntil must be a date string');
      }
    }
    
    if (method === 'arima' || method === 'ARIMA') {
      // ARIMA specific validations
      if (config.p && (typeof config.p !== 'number' || config.p < 0)) {
        errors.push('p (autoregressive order) must be a non-negative number');
      }
      if (config.d && (typeof config.d !== 'number' || config.d < 0)) {
        errors.push('d (differencing order) must be a non-negative number');
      }
      if (config.q && (typeof config.q !== 'number' || config.q < 0)) {
        errors.push('q (moving average order) must be a non-negative number');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

/**
 * Convenience function for creating predictors
 * @param {string} method - The prediction method
 * @param {Object} config - Predictor configuration
 * @returns {BasePredictor} - Predictor instance
 */
export function createPredictor(method, config) {
  return PredictorFactory.createPredictor(method, config);
}

// Export available methods for convenience
export const PREDICTION_METHODS = {
  LINEAR_REGRESSION: 'linear_regression',
  MOVING_AVERAGE: 'moving_average',
  ARIMA: 'arima',
  EXPONENTIAL_SMOOTHING: 'exponential_smoothing',
  // Future methods
  // POLYNOMIAL: 'polynomial'
};

export default PredictorFactory;